import { SHOW_TASK_ASSIGNEE_ROLES } from './taskBoardConfig';
import { useTaskTranslation } from './hooks/useTaskTranslation';
import { MultiSelectFilter } from '../../components/UI/Filters/MultiSelectFilter';
import PageTitle from '../../components/UI/PageTitle/PageTitle';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KanbanView } from './components/KanbanView';
import { TaskTable } from './components/TaskTable';
import { EditTaskModal } from './components/EditTaskModal';
import { CreateTaskModal } from './components/CreateTaskModal';
import styles from './TaskBoard.module.css';
import searchIcon from '../../assets/icons/searchIcon.svg';
import listIcon from '../../assets/icons/listIcon.svg';
import gridIcon from '../../assets/icons/gridIcon.svg';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  Task,
  fetchTasks,
  selectTasks,
  selectTasksStatus,
  TASK_STATUSES,
  TASK_PRIORITIES,
  TaskPriority,
} from '../../store/slices/tasks';
import { TaskContextProvider, useTaskContext } from '../../context/TaskContext';
import { TaskStatus } from '../../store/slices/tasks';
import { toast } from 'react-toastify';
import { taskMatchesFilters } from './taskFilters';
import WithLoader from '../../components/WithLoader/WithLoader';
import { useTaskAssigneeSelection } from './hooks/useTaskAssigneeSelection';
import {
  findTaskCustomer,
  parseTaskId,
  taskAssigneeDesignation,
  taskDesignationLabel,
  taskStatusLabel,
} from './taskUtils';
import { selectLocationId } from '../../store/slices/auth';
import {
  fetchCustomers,
  selectCustomers,
  selectCustomersStatus,
} from '../../store/slices/customers';

export const TaskBoard = () => (
  <TaskContextProvider>
    <TaskBoardContent />
  </TaskContextProvider>
);

const TaskBoardContent = () => {
  const { translations, taskText } = useTaskTranslation();
  const dispatch = useAppDispatch();
  const tasks = useAppSelector(selectTasks);
  const locationId = useAppSelector(selectLocationId);
  const customers = useAppSelector(selectCustomers);
  const customersStatus = useAppSelector(selectCustomersStatus);
  const requestedCustomersLocation = useRef<string | null>(null);
  const assigneeSelection = useTaskAssigneeSelection();
  const persons = useMemo(
    () => assigneeSelection.options.map(({ person }) => person),
    [assigneeSelection.options]
  );
  const status = useAppSelector(selectTasksStatus);
  const [variant, setVariant] = useState<'kanban' | 'detailed' | 'table'>('kanban');
  const [previousBoardVariant, setPreviousBoardVariant] = useState<'kanban' | 'detailed'>('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<number[]>([]);
  const [assigneeRoleFilter, setAssigneeRoleFilter] = useState<string[]>([]);
  const [customerFilter, setCustomerFilter] = useState<number[]>([]);
  const [creatingTask, setCreatingTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const hasActiveFilters = Boolean(
    searchQuery ||
      statusFilter.length > 0 ||
      priorityFilter.length > 0 ||
      assigneeFilter.length > 0 ||
      assigneeRoleFilter.length > 0 ||
      customerFilter.length > 0
  );
  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setPriorityFilter([]);
    setAssigneeFilter([]);
    setAssigneeRoleFilter([]);
    setCustomerFilter([]);
  };

  const taskSocket = useTaskContext();
  const { setSelectedTaskId } = taskSocket;

  useEffect(() => {
    setSelectedTaskId(selectedTask?.task_id);
  }, [selectedTask?.task_id, setSelectedTaskId]);

  const handleStatusChange = async (taskId: number, status: TaskStatus) => {
    try {
      await taskSocket.updateTask({ taskId, status });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : translations('task_board.unable_to_update_task'),
        {
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
    }
  };

  useEffect(() => {
    dispatch(fetchTasks());
  }, [dispatch]);

  useEffect(() => {
    if (
      locationId &&
      customers.length === 0 &&
      customersStatus === 'idle' &&
      requestedCustomersLocation.current !== locationId
    ) {
      requestedCustomersLocation.current = locationId;
      dispatch(fetchCustomers());
    }
  }, [dispatch, locationId, customers.length, customersStatus]);

  const taskCustomers = useMemo(() => {
    const options = new Map<number, { id: number; name: string }>();
    tasks.forEach((task) => {
      const id = parseTaskId(task.customer_id);
      if (id === undefined) return;
      const customer = findTaskCustomer(id, customers);
      const name = [customer?.person_firstname, customer?.person_lastname]
        .filter(Boolean)
        .join(' ')
        .trim();
      options.set(id, { id, name });
    });
    return Array.from(options.values()).sort((first, second) => first.id - second.id);
  }, [tasks, customers]);

  const taskAssignees = useMemo(() => {
    const assignees = new Map<number, { id: number; name: string; designation: string }>();
    tasks.forEach((task) => {
      const id = parseTaskId(task.person_id_assign);
      if (id === undefined) return;
      const name = task.person_name_assign?.trim() || assignees.get(id)?.name || '';
      const designation =
        taskAssigneeDesignation(task, persons) || assignees.get(id)?.designation || '';
      assignees.set(id, { id, name, designation });
    });
    return Array.from(assignees.values()).sort(
      (first, second) => first.name.localeCompare(second.name) || first.id - second.id
    );
  }, [tasks, persons]);

  const assigneeRoles = useMemo(
    () =>
      Array.from(new Set(tasks.map((task) => taskAssigneeDesignation(task, persons))))
        .filter(Boolean)
        .sort((first, second) => first.localeCompare(second)),
    [tasks, persons]
  );

  const visibleTasks = useMemo(
    () =>
      tasks.filter((task) =>
        taskMatchesFilters(
          task,
          {
            searchQuery,
            statuses: statusFilter,
            priorities: priorityFilter,
            assigneeIds: assigneeFilter,
            assigneeRoles: assigneeRoleFilter,
            customerIds: customerFilter,
          },
          taskAssigneeDesignation(task, persons)
        )
      ),
    [
      tasks,
      persons,
      searchQuery,
      statusFilter,
      priorityFilter,
      assigneeFilter,
      assigneeRoleFilter,
      customerFilter,
    ]
  );

  return (
    <div className={styles.board}>
      <PageTitle className={styles.pageTitle}>{translations('task_board.task_board')}</PageTitle>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchWrapper}>
            <img src={searchIcon} className={styles.searchIcon} alt="" />
            <input
              className={styles.searchInput}
              type="search"
              aria-label={translations('task_board.search_tasks')}
              placeholder={translations('task_board.search_by_task_id_title_or_customer_id')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
          <MultiSelectFilter
            label={translations('task_board.statuses')}
            placeholder={translations('task_board.all_statuses')}
            ariaLabel={translations('task_board.filter_by_status')}
            options={TASK_STATUSES.map((value) => ({
              value,
              label: taskStatusLabel(value, translations),
            }))}
            selectedValues={statusFilter}
            onChange={setStatusFilter}
          />
          <MultiSelectFilter
            label={translations('task_board.priority')}
            placeholder={translations('task_board.all_priorities')}
            ariaLabel={translations('task_board.filter_by_priority')}
            options={TASK_PRIORITIES.map((value) => ({
              value,
              label: translations(`task_board.priority_${value}`),
            }))}
            selectedValues={priorityFilter}
            onChange={setPriorityFilter}
          />
          <MultiSelectFilter
            label={translations('task_board.assignees')}
            placeholder={translations('task_board.all_assignees')}
            ariaLabel={translations('task_board.filter_by_assignee')}
            options={taskAssignees.map(({ id, name, designation }) => ({
              value: id,
              label: name || taskText('task_board.person_id', { id }),
              description: SHOW_TASK_ASSIGNEE_ROLES
                ? taskDesignationLabel(designation, translations)
                : undefined,
            }))}
            selectedValues={assigneeFilter}
            onChange={setAssigneeFilter}
          />
          {SHOW_TASK_ASSIGNEE_ROLES && (
            <MultiSelectFilter
              label={translations('task_board.roles')}
              placeholder={translations('task_board.all_roles')}
              ariaLabel={translations('task_board.filter_by_assignee_role')}
              options={assigneeRoles.map((role) => ({
                value: role,
                label: taskDesignationLabel(role, translations),
              }))}
              selectedValues={assigneeRoleFilter}
              onChange={setAssigneeRoleFilter}
              disabled={!assigneeSelection.ready}
            />
          )}
          <MultiSelectFilter
            label={translations('task_board.customers')}
            placeholder={translations('task_board.all_customers')}
            ariaLabel={translations('task_board.filter_by_customer')}
            options={taskCustomers.map(({ id, name }) => ({
              value: id,
              label: name || taskText('task_board.customer_id', { id }),
            }))}
            selectedValues={customerFilter}
            onChange={setCustomerFilter}
          />
          <button
            type="button"
            className={`${styles.newTaskButton} ${styles.resetFiltersButton}`}
            disabled={!hasActiveFilters}
            onClick={resetFilters}
          >
            {translations('task_board.reset_filters')}
          </button>
        </div>
        <div className={styles.headerRight}>
          <button
            type="button"
            className={styles.newTaskButton}
            onClick={() => {
              setCreatingTask(true);
            }}
          >
            {translations('task_board.new_task')}
          </button>
          <div className={styles.viewControls}>
            <button
              className={`${styles.viewButton} ${variant === 'kanban' ? styles.activeView : ''}`}
              onClick={() => setVariant('kanban')}
              title={translations('task_board.compact_view')}
              aria-pressed={variant === 'kanban'}
            >
              <img src={listIcon} alt={translations('task_board.compact_view')} />
            </button>
            <button
              className={`${styles.viewButton} ${variant === 'detailed' ? styles.activeView : ''}`}
              onClick={() => setVariant('detailed')}
              title={translations('task_board.detailed_view')}
              aria-pressed={variant === 'detailed'}
            >
              <img src={gridIcon} alt={translations('task_board.detailed_view')} />
            </button>
          </div>
          <button
            type="button"
            className={styles.viewAllButton}
            onClick={() => {
              if (variant === 'table') {
                setVariant(previousBoardVariant);
              } else {
                setPreviousBoardVariant(variant);
                setVariant('table');
              }
            }}
            aria-pressed={variant === 'table'}
          >
            {variant === 'table'
              ? translations('task_board.back_to_board')
              : translations('task_board.view_all')}
            <span aria-hidden="true">{variant === 'table' ? '←' : '→'}</span>
          </button>
        </div>
      </header>
      <WithLoader isLoading={(status === 'idle' || status === 'pending') && tasks.length === 0}>
        {status === 'fulfilled' && visibleTasks.length === 0 && (
          <div role="status" className={styles.message}>
            {tasks.length
              ? translations('task_board.no_tasks_match_your_search_or_filters')
              : translations('task_board.no_tasks_available')}
          </div>
        )}
        {taskSocket.connectionError && (
          <div role="status" className={styles.message}>
            {taskSocket.connectionError}{' '}
            {translations('task_board.changes_will_be_available_after_reconnection')}
          </div>
        )}
        {variant === 'table' ? (
          <TaskTable
            tasks={visibleTasks}
            onTaskClick={setSelectedTask}
            canUpdate={taskSocket.connected && taskSocket.pendingTaskIds.length === 0}
            pendingTaskIds={taskSocket.pendingTaskIds}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <KanbanView
            tasks={visibleTasks}
            variant={variant}
            onTaskClick={setSelectedTask}
            canUpdate={taskSocket.connected && taskSocket.pendingTaskIds.length === 0}
            pendingTaskIds={taskSocket.pendingTaskIds}
            onStatusChange={handleStatusChange}
          />
        )}
      </WithLoader>
      {creatingTask && (
        <CreateTaskModal
          onClose={() => setCreatingTask(false)}
          onCreated={() => {
            setCreatingTask(false);
            resetFilters();
            dispatch(fetchTasks());
          }}
        />
      )}
      {selectedTask && (
        <EditTaskModal
          key={selectedTask.task_id}
          task={selectedTask}
          taskSocket={taskSocket}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </div>
  );
};
